import AsyncEventHandler from "../asyncEventHandler";
import { Role, TokenState } from "./types";
import { RolesDocument, RolesQuery } from "./queries.generated";
import { ApolloClient, NextLink, Operation, Observable, FetchResult } from "@apollo/client";
import { OperationDefinitionNode } from "graphql";
import { BackendErrorCode, TStore } from "../types";
import { getMainDefinition } from "@apollo/client/utilities";

export type ErrorHandlerResult = {
    handled?: boolean;
    return?: Observable<FetchResult>;
};

export abstract class AuthManagerBase {
    protected tokenState: TokenState = TokenState.Unknown;
    protected readonly tokenStateEvent: AsyncEventHandler;
    /** Triggered when the roles of the user may have changed and have to be fetched again. */
    readonly rolesInvalidatedEvent: AsyncEventHandler;
    readonly apolloClient: ApolloClient<any>;
    readonly store: TStore;

    constructor(apolloClient: ApolloClient<any>, store: TStore) {
        this.tokenStateEvent = new AsyncEventHandler("accessTokenState");
        this.rolesInvalidatedEvent = new AsyncEventHandler("rolesInvalidated");
        this.apolloClient = apolloClient;
        this.store = store;
    }

    abstract refreshToken(): Promise<void>;

    /**
     * The roles are always fetched from the Conference API, which gets them from Heimdall and caches them in redis.
     *
     * Throws when the roles cannot be fetched, so that "the user has no roles" and "the roles are unknown" can be
     * told apart.
     */
    async fetchRoles(): Promise<Role[]> {
        // Authorization data has to be fresh, it must never come from the Apollo cache.
        const result = await this.apolloClient.query<RolesQuery>({ query: RolesDocument, fetchPolicy: "no-cache" });

        this.tokenState = TokenState.Valid;
        this.tokenStateEvent.trigger();

        return result.data.roles as Role[];
    }

    abstract login(): Promise<void>;

    abstract get isLoggedIn(): boolean;

    abstract get authHeader(): string;

    abstract handleGraphQLError(code: BackendErrorCode, operation: Operation, forward: NextLink): ErrorHandlerResult;

    abstract get userName(): string;

    setAuthHeaders(operation: Operation) {
        if (operation.getContext().noAuth) return;

        const mainDefinition = getMainDefinition(operation.query) as OperationDefinitionNode;
        if (mainDefinition.operation === "subscription") return;

        const extraHeaders = this.headers;

        console.debug("set extra headers %o to operation %s", extraHeaders, operation.operationName);
        operation.setContext(({ headers }: { headers?: Record<string, string> }) => ({
            headers: {
                ...headers,
                ...extraHeaders,
            },
        }));
    }

    get headers(): Record<string, string> {
        if (!this.isLoggedIn) return {};

        return {
            authorization: this.authHeader,
        };
    }

    async waitForValidToken(): Promise<void> {
        if (this.tokenState !== TokenState.Valid) await this.tokenStateEvent.waitFor();
    }
}
